/**
 * friendlyAuthError — maps transient INFRASTRUCTURE failures on the
 * credential endpoints to one calm human message, so raw Cloudflare /
 * axios text ("could not parse", "timeout of 30000ms exceeded", HTML
 * error pages) never renders on the sign-in screen. Runs AFTER the
 * api.js auto-retries are exhausted (2026-07 pod-rollover fix).
 * Returns null for normal application errors (wrong password, rate
 * limit, validation) so callers fall through to their existing
 * `detail` extraction.
 */
export const TRANSIENT_AUTH_MESSAGE =
  "Couldn't reach the game server — please try again in a moment.";

// Infra-layer statuses: gateway/proxy errors + the Cloudflare 52x family.
const INFRA_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);

export function friendlyAuthError(error) {
  if (!error || error.misconfigured) return null;
  const response = error.response;
  // 2026-08-11 (App Store rejection RCA, mobile parity) — only claim
  // "couldn't reach the server" for genuine transport failures; a plain JS
  // exception in the post-login pipeline must surface its REAL message.
  if (!response) {
    const isTransport = !!(error.isAxiosError || error.request !== undefined || error.code);
    return isTransport ? TRANSIENT_AUTH_MESSAGE : null;
  }
  if (INFRA_STATUSES.has(response.status)) return TRANSIENT_AUTH_MESSAGE;
  const data = response.data;
  // A string body (Cloudflare/ingress HTML or plain-text page) on any
  // 5xx is an infra error, never an app response.
  if (typeof data === 'string') {
    return response.status >= 500 ? TRANSIENT_AUTH_MESSAGE : null;
  }
  // 5xx without a FastAPI `detail` field = the app never answered.
  if (response.status >= 500 && (data == null || data.detail == null)) {
    return TRANSIENT_AUTH_MESSAGE;
  }
  return null;
}
